import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Field } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import formStyles from '../../../styles/forms.module.scss';
import s from '../WindFacilityForm.module.scss';
import {
  TextInput,
  CheckboxInput,
  AccessibleDatepickerInput,
  AddressInputFields,
  CommonFacilityFields,
} from '../../../common/FormFields';
import RemoveFacilityButton from '../FacilityButtons/RemoveFacilityButton';
import InfoBubble from '../../../common/InfoBubble/InfoBubble';
import DuplicateFacilityButton from '../FacilityButtons/DuplicateFacilityButton';

function WindFacilityForm(props) {
  const { id, index, deleteFacility, values, form, arrayHelpers } = props;
  const namePrefixMin = 'windFacilityData';
  const namePrefix = `${namePrefixMin}[${index}]`;

  /* Always move the focus to the newly added facility, when the user
  used the 'add another facility' button */
  const firstInputRef = useRef(null);
  useEffect(() => {
    if (index !== 0 && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  const duplicateFacility = () => {
    arrayHelpers.insert(index + 1, {
      ...values[namePrefixMin][index],
      id: uuidv4(),
    });
  };

  return (
    <div className={formStyles.inputGroup}>
      {index !== 0 && (
        <>
          <hr className={formStyles.separator} />
          <div className={s.removeWrapper}>
            <RemoveFacilityButton onClick={() => deleteFacility(index)} />
          </div>
        </>
      )}
      <fieldset>
        <legend className={formStyles.caption}>
          <FormattedMessage id="offerWizard.StepFacilityData.technicalDetails" />
        </legend>
        <div ref={firstInputRef} tabIndex={-1}>
          <div data-id={id} className={formStyles.inputPair}>
            <Field
              name={`${namePrefix}.hubHeight`}
              component={TextInput}
              labelMsg="offerWizard.StepWindFacilityData.hubHeight.label"
            />
            <Field
              name={`${namePrefix}.diameter`}
              component={TextInput}
              labelMsg="offerWizard.StepWindFacilityData.diameter"
            />
          </div>
        </div>
        <div className={formStyles.inputPair}>
          <Field
            name={`${namePrefix}.output`}
            component={TextInput}
            labelMsg="offerWizard.StepWindFacilityData.output"
          />
          <Field
            name={`${namePrefix}.powerDensity`}
            component={TextInput}
            labelMsg="offerWizard.StepWindFacilityData.powerDensity"
          />
        </div>
        <div className={formStyles.inputPair}>
          <Field
            name={`${namePrefix}.manufacturer`}
            component={TextInput}
            labelMsg="offerWizard.StepWindFacilityData.manufacturer"
          />
          <Field
            name={`${namePrefix}.model`}
            component={TextInput}
            labelMsg="offerWizard.StepWindFacilityData.model"
          />
        </div>
        <div className={formStyles.inputPairSingle}>
          <Field
            name={`${namePrefix}.initialOperation`}
            component={AccessibleDatepickerInput}
            labelMsg="offerWizard.StepWindFacilityData.initialOperation.label"
          />
        </div>
        <div className={formStyles.inputWithVerticalSpacing}>
          <div className={s.checkBoxWithInfoIcon}>
            <Field
              name={`${namePrefix}.paragraph51`}
              labelContent={
                <FormattedMessage id="offerWizard.StepWindFacilityData.paragraph51" />
              }
              component={CheckboxInput}
            />
            {/* <ErrorMessage name={`${namePrefix}.paragraph51`} /> */}
            <div className={s.infoIcon}>
              <InfoBubble>
                <FormattedMessage id="offerWizard.StepWindFacilityData.paragraph51.text" />
              </InfoBubble>
            </div>
          </div>
          <Field
            name={`${namePrefix}.paymentNegativeHours`}
            labelContent={
              <FormattedMessage id="offerWizard.StepWindFacilityData.paymentNegativeHours" />
            }
            component={CheckboxInput}
          />
        </div>
      </fieldset>
      <CommonFacilityFields
        namePrefixMin={namePrefixMin}
        values={values}
        index={index}
        form={form}
      />
      <div className={formStyles.inputWithVerticalSpacing}>
        <fieldset>
          <legend className={formStyles.caption}>
            <FormattedMessage id="offerWizard.StepWindFacilityData.address" />
          </legend>
          <AddressInputFields namePrefix={namePrefix} isRequired={false} />
        </fieldset>
      </div>
      <DuplicateFacilityButton onClick={() => duplicateFacility()} />
    </div>
  );
}

WindFacilityForm.propTypes = {
  /** The formik id of this facility */
  id: PropTypes.string.isRequired,
  /** current index of the added facility */
  index: PropTypes.number.isRequired,
  /** Formik values */
  values: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  /** Formik form object */
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  /** Function for removing this facility */
  deleteFacility: PropTypes.func.isRequired,
  /** Formik array helpers */
  arrayHelpers: PropTypes.shape({
    insert: PropTypes.func.isRequired,
  }).isRequired,
};

export default WindFacilityForm;
